import { useState, useEffect } from "react";
import { useUser } from "@clerk/react";
import { Link } from "react-router-dom";
import { apiFetch } from "../utils/apiClient";
import { getReviewAlertPreference, upsertReviewAlertPreference } from "../utils/alertsApi";

function formatMoney(n) {
  return `$${Math.round(n || 0).toLocaleString()}`;
}

const LIVE_NOTES = {
  now_disclosed: "The provider's questionnaire now discloses this company. You may want to reverse this review.",
  not_in_cms: "CMS no longer shows this relationship for the period reviewed.",
  now_in_cms: "CMS now shows activity for this provider and company. It appears as a Known-Vendor Overlap finding, which needs its own review.",
  no_policy_snapshot: "This review has no recorded settings, so amounts are not being compared.",
};

function describePolicy(snapshot) {
  const t = snapshot.thresholds || {};
  const part = (label, v) =>
    v === null || v === undefined ? `${label}: not checked` : Number(v) === 0 ? `${label}: any amount` : `${label} ≥ ${formatMoney(v)}`;
  const w = snapshot.window;
  const years = w ? (w.startYear === w.endYear ? `program year ${w.startYear}` : `program years ${w.startYear}–${w.endYear}`) : null;
  return [part("general payments", t.general), part("ownership", t.ownership), part("direct research", t.directResearch), part("PI research", t.piResearch)].join("; ")
    + (years ? ` · ${years}` : "");
}

function policyDiffers(snapshot, current) {
  if (!snapshot || !current) return false;
  const norm = (v) => (v === null || v === undefined ? null : Number(v));
  const s = snapshot.thresholds || {};
  const c = current.thresholds || {};
  if (["general", "ownership", "directResearch", "piResearch"].some((k) => norm(s[k]) !== norm(c[k]))) return true;
  return snapshot.lookbackYears !== undefined && (snapshot.lookbackYears ?? null) !== (current.lookbackYears ?? null);
}

const STATUS_META = {
  holding:      { label: "Holding",      textClass: "text-green-400",  bgClass: "bg-green-500/10",  borderClass: "border-green-500/30" },
  needs_review: { label: "Needs Review", textClass: "text-amber-300",  bgClass: "bg-amber-500/10",  borderClass: "border-amber-500/40" },
  reversed:     { label: "Reversed",     textClass: "text-white/40",   bgClass: "bg-white/[0.03]",  borderClass: "border-white/10" },
};

export default function ReviewedFindingsPage() {
  const { user } = useUser();
  const [excuses, setExcuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expandedHistory, setExpandedHistory] = useState({});
  const [reminderFrequency, setReminderFrequency] = useState("none");
  const [currentPolicy, setCurrentPolicy] = useState(null);

  useEffect(() => {
    if (!user) return;
    apiFetch("/api/list-excuses", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: user.id }),
    })
      .then((res) => res.json())
      .then((data) => setExcuses(data.excuses || []))
      .catch(() => setExcuses([]))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    if (!user) return;
    getReviewAlertPreference(user.id)
      .then((data) => setReminderFrequency(data?.reminder_frequency || "none"))
      .catch(() => setReminderFrequency("none"));
  }, [user]);

  const handleReminderChange = (value) => {
    const previous = reminderFrequency;
    setReminderFrequency(value); // optimistic
    upsertReviewAlertPreference(user.id, value)
      .then((result) => {
        if (!result?.success) {
          console.error("Failed to save reminder preference:", result?.error);
          setReminderFrequency(previous);
        }
      })
      .catch(() => setReminderFrequency(previous));
  };

  useEffect(() => {
    if (!user) return;
    apiFetch("/api/manage-coi-disclosure-thresholds", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: user.id, action: "get" }),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data?.settings) return;
        const s = data.settings;
        setCurrentPolicy({
          thresholds: {
            general: s.generalPaymentsThreshold, ownership: s.ownershipThreshold,
            directResearch: s.directResearchThreshold, piResearch: s.piResearchThreshold,
          },
          lookbackYears: s.lookbackYears,
        });
      })
      .catch(() => {});
  }, [user?.id]);

  const filtered = excuses.filter((e) => {
    if (statusFilter === "settings_changed") {
      if (!(e.status === "active" && e.disclosure_policy_snapshot && policyDiffers(e.disclosure_policy_snapshot, currentPolicy))) return false;
    } else if (statusFilter !== "all" && e.liveStatus !== statusFilter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      if (!(e.provider_name?.toLowerCase().includes(q) || e.company_name?.toLowerCase().includes(q) || e.npi?.includes(q))) return false;
    }
    return true;
  });

  const counts = {
    holding: excuses.filter((e) => e.liveStatus === "holding").length,
    needs_review: excuses.filter((e) => e.liveStatus === "needs_review").length,
    reversed: excuses.filter((e) => e.liveStatus === "reversed").length,
  };

  if (loading) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-10">
        <p className="text-white/40 text-sm">Loading reviewed findings…</p>
      </main>
    );
  }

  return (
    <main className="max-w-3xl mx-auto px-4 py-10">
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-display text-2xl text-white">Reviewed Findings</h1>
        <div className="flex items-center gap-2">
          <span className="text-white/40 text-xs">Repeat reminders</span>
          <select
            value={reminderFrequency}
            onChange={(e) => handleReminderChange(e.target.value)}
            className="bg-navy-700 border border-white/10 text-white text-xs rounded-lg px-2 py-1 focus:outline-none focus:border-white/30"
          >
            <option value="none">None (single alert)</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      </div>
      <p className="text-white/40 text-xs mb-6">
        {counts.holding} active · {counts.needs_review} needs review · {counts.reversed} reversed
      </p>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <input
          type="text"
          placeholder="Search provider or company…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-navy-700 border border-white/10 text-white text-xs rounded-lg px-3 py-1.5 flex-1 min-w-[200px] focus:outline-none focus:border-white/30"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-navy-700 border border-white/10 text-white text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:border-white/30"
        >
          <option value="all">All statuses</option>
          <option value="holding">Holding</option>
          <option value="needs_review">Needs Review</option>
          <option value="reversed">Reversed</option>
          <option value="settings_changed">Settings changed since review</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-white/40 text-sm">No reviewed findings match your filters.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((e) => {
            const meta = STATUS_META[e.liveStatus] || STATUS_META.holding;
            return (
              <div key={e.id} className={`card border ${meta.borderClass} ${meta.bgClass}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <p className="text-white/80 text-sm font-medium">
                      {e.signal_type} — {e.provider_name || `NPI ${e.npi}`} · {e.company_name}
                    </p>
                    <p className="text-white/40 text-xs mt-0.5">
                      Reviewed by {e.reviewed_by_user_id} · {new Date(e.reviewed_at).toLocaleDateString()}
                      {e.status === "reversed" && e.reversed_at && (
                        <> · Reversed by {e.reversed_by_user_id} · {new Date(e.reversed_at).toLocaleDateString()}</>
                      )}
                    </p>
                    {e.review_notes && <p className="text-white/30 text-xs mt-0.5 italic">"{e.review_notes}"</p>}
                    {e.disclosure_policy_snapshot && (
                      <p className="text-white/30 text-xs mt-0.5">
                        Reviewed under: {describePolicy(e.disclosure_policy_snapshot)}
                        {e.status === "active" && policyDiffers(e.disclosure_policy_snapshot, currentPolicy) && (
                          <span className="ml-2 text-amber-400/80">⚠ Settings changed since review</span>
                        )}
                      </p>
                    )}
                    {e.liveNote && LIVE_NOTES[e.liveNote] && (
                      <p className="text-white/40 text-xs mt-0.5">{LIVE_NOTES[e.liveNote]}</p>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full border shrink-0 ${meta.textClass} ${meta.borderClass}`}>
                    {e.liveStatus === "needs_review" ? "⚠ " : e.liveStatus === "holding" ? "✓ " : ""}{meta.label}
                  </span>
                </div>
                {e.liveStatus === "needs_review" && (
                  <p className="text-amber-400/70 text-xs mt-2">
                    {e.liveReason === "tier_changed"
                      ? "This relationship's risk category has changed since review, or no longer shows this signal at all."
                      : (e.liveBreaches || []).map((b) =>
                          `${b.category} is now ${formatMoney(b.current)} (was ${formatMoney(b.previous)} at review) — over your ${formatMoney(b.threshold)} threshold`
                        ).join("; ")}
                  </p>
                )}
                {!e.excuse_regardless_of_amount && e.status === "active" && (
                  <p className="text-white/25 text-xs mt-2">
                    Thresholds — General: {e.threshold_general_amount != null ? formatMoney(e.threshold_general_amount) : "any"},{" "}
                    Ownership: {e.threshold_ownership_amount != null ? formatMoney(e.threshold_ownership_amount) : "any"}
                    {e.signal_type === "Payment Audit Threshold" ? (
                      <>
                        , Direct research: {e.threshold_direct_research_amount != null ? formatMoney(e.threshold_direct_research_amount) : "any"},{" "}
                        PI research: {e.threshold_pi_research_amount != null ? formatMoney(e.threshold_pi_research_amount) : "any"}
                      </>
                    ) : (
                      <>, Research: {e.threshold_research_amount != null ? formatMoney(e.threshold_research_amount) : "any"}</>
                    )}
                  </p>
                )}
                <div className="mt-2 flex items-center gap-3">
                  {e.status === "active" && (
                    <Link to={`/physician/${e.npi}?name=${encodeURIComponent(e.provider_name || "")}`} className="text-teal-400/70 hover:text-teal-400 text-xs">
                      View provider →
                    </Link>
                  )}
                  {e.history && e.history.length > 0 && (
                    <button
                      onClick={() => setExpandedHistory((prev) => ({ ...prev, [e.id]: !prev[e.id] }))}
                      className="text-white/30 hover:text-white/60 text-xs"
                    >
                      {expandedHistory[e.id] ? "▲" : "▼"} {e.history.length} earlier review{e.history.length !== 1 ? "s" : ""}
                    </button>
                  )}
                </div>
                {expandedHistory[e.id] && (
                  <div className="mt-2 pt-2 border-t border-white/10 space-y-2">
                    {e.history.map((h) => (
                      <div key={h.id} className="text-xs text-white/35">
                        <p>
                          Reviewed by {h.reviewed_by_user_id} · {new Date(h.reviewed_at).toLocaleDateString()}
                          {h.status === "reversed" && h.reversed_at && (
                            <> · Reversed by {h.reversed_by_user_id} · {new Date(h.reversed_at).toLocaleDateString()}</>
                          )}
                        </p>
                        {h.review_notes && <p className="italic mt-0.5">"{h.review_notes}"</p>}
                        {h.disclosure_policy_snapshot && <p className="mt-0.5">Reviewed under: {describePolicy(h.disclosure_policy_snapshot)}</p>}
                        {h.status === "superseded" && <p className="mt-0.5">Replaced by a later review</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </main>
  );
}